import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Download, RefreshCw, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/heat";
import { ScoringCard } from "@/components/ScoringCard";
import { InsightsEditor } from "@/components/InsightsEditor";
import { TopicDescriptionsEditor } from "@/components/TopicDescriptionsEditor";

const MAP_ROWS = [
  { key: "dob", label: "Date of birth", std: [["std:dateOfBirth", "Standard: Date of birth"]] },
  { key: "gender", label: "Gender", std: [["std:gender", "Standard: Gender"]] },
  { key: "organization", label: "Church / Organization", std: [["std:companyName", "Standard: Business name"]] },
  { key: "city", label: "City", std: [["std:city", "Standard: City"]] },
  { key: "state", label: "State", std: [["std:state", "Standard: State"]] },
  { key: "entry_type", label: "Self or group assessment" },
  { key: "race", label: "Race / Ethnicity" },
  { key: "event_group", label: "Event & Client Groups" },
  { key: "submitted_at", label: "Submission date (optional)", std: [["std:dateUpdated", "Standard: Last updated"], ["std:dateAdded", "Standard: Date added"]] },
  { key: "report_url", label: "Report link (written back)" },
];

const STEPS = [
  "The app reads each quiz answer saved on the contact: one answer for each of the 25 “(Rate Yourself)” topics. Topics in two areas give the same points to both. It adds them up into Body, Mind, Spirit, Self and Others using your question chart.",
  "In GHL, create a contact custom field (Single line text) called “Report Link”, click “Load GHL fields”, choose it in the “Report link” row, then Save.",
  "Create a GHL Workflow triggered when “Report Link” changes, with an Email/SMS step that sends {{contact.report_link}} to the person. That is how their report reaches them.",
  "Once published, the app checks GHL every 15 minutes. Use “Sync now” any time to pull results immediately.",
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [ghlFields, setGhlFields] = useState([]);
  const [busy, setBusy] = useState("");
  const [syncCount, setSyncCount] = useState(0);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setS({ ...r.data, report_base_url: r.data.report_base_url || window.location.origin });
      if (r.data.location_id) api.get("/ghl/custom-fields").then((f) => setGhlFields(f.data.fields)).catch(() => {});
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!s) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#1F4266]" />;

  const setField = (k, v) => setS({ ...s, fields: { ...s.fields, [k]: v === "none" ? "" : v } });
  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const { data } = await api.put("/settings", { location_id: s.location_id, report_base_url: s.report_base_url, fields: s.fields, points: s.points });
    setS({ ...data, report_base_url: data.report_base_url || window.location.origin });
    toast.success("Settings saved");
  });
  const loadFields = () => run("fields", async () => {
    await api.put("/settings", { location_id: s.location_id, report_base_url: s.report_base_url, fields: s.fields });
    const { data } = await api.get("/ghl/custom-fields");
    setGhlFields(data.fields);
    toast.success(`Loaded ${data.fields.length} custom fields from GHL`);
  });
  const sync = () => run("sync", async () => {
    await api.put("/settings", { location_id: s.location_id, report_base_url: s.report_base_url, fields: s.fields, points: s.points });
    const { data } = await api.post("/ghl/sync");
    setSyncCount((n) => n + 1);
    toast.success(`Scanned ${data.contacts_scanned} contacts · ${data.new_submissions} new · ${data.report_links_sent} links sent`);
    const r = await api.get("/settings");
    setS({ ...r.data, report_base_url: r.data.report_base_url || window.location.origin });
  });
  const resend = () => {
    if (!window.confirm("Write a fresh report link to every person in GHL? If your GHL workflow sends the link when it changes, everyone will receive it again.")) return;
    run("resend", async () => {
      await api.put("/settings", { location_id: s.location_id, report_base_url: s.report_base_url, fields: s.fields, points: s.points });
      const { data } = await api.post("/ghl/resend-links");
      toast.success(data.links_paused_in_preview ? "Preview copy: links are only written to GHL by the published app" : `${data.report_links_sent} report links written to GHL`);
    });
  };
  const removeSamples = () => run("samples", async () => {
    const { data } = await api.delete("/submissions-samples");
    toast.success(`Removed ${data.deleted} sample entries`);
    setS({ ...s, sample_count: 0 });
  });

  const res = s.last_sync_result;

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Go High Level connection</div>
        <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F4266]">Settings</h1>
      </div>
      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-8">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 text-sm" data-testid="ghl-token-status">
              {s.token_configured ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
              Private Integration token {s.token_configured ? "is saved securely on the server" : "is missing"}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Location (sub-account) ID</Label>
                <Input data-testid="settings-location-id" value={s.location_id} placeholder="e.g. ve9EPM428h8vShlRW1KT" onChange={(e) => setS({ ...s, location_id: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Report link base address</Label>
                <Input data-testid="settings-base-url" value={s.report_base_url} onChange={(e) => setS({ ...s, report_base_url: e.target.value })} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="load-ghl-fields-button" variant="outline" className="rounded-full" onClick={loadFields} disabled={!!busy || !s.location_id}>
                <Download className="mr-2 h-4 w-4" /> Load GHL fields
              </Button>
              <Button data-testid="save-settings-button" className="rounded-full bg-[#1F4266] hover:bg-[#122A43]" onClick={save} disabled={!!busy}>
                <Save className="mr-2 h-4 w-4" /> Save
              </Button>
              <Button data-testid="settings-sync-button" variant="outline" className="rounded-full" onClick={sync} disabled={!!busy}>
                <RefreshCw className={`mr-2 h-4 w-4 ${busy === "sync" ? "animate-spin" : ""}`} /> Sync now
              </Button>
              <Button data-testid="resend-links-button" variant="outline" className="rounded-full" onClick={resend} disabled={!!busy}>
                Resend all report links
              </Button>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">Field mapping</h2>
            <p className="mt-1 text-sm text-slate-500">Tell the app which GHL contact field holds each detail about the person.</p>
            <div className="mt-5 divide-y divide-slate-100">
              {MAP_ROWS.map((row) => {
                const current = s.fields[row.key] || "none";
                const known = current === "none" || current.startsWith("std:") || ghlFields.some((f) => f.id === current);
                return (
                  <div key={row.key} className="grid gap-2 py-3 sm:grid-cols-[1fr_1.4fr] sm:items-center">
                    <div className="text-sm font-medium text-slate-800">
                      {row.label} {row.required && <span className="text-red-500">*</span>}
                    </div>
                    <Select value={current} onValueChange={(v) => setField(row.key, v)}>
                      <SelectTrigger data-testid={`map-${row.key}`} className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        <SelectItem value="none">— Not mapped —</SelectItem>
                        {(row.std || []).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        {!known && <SelectItem value={current}>Saved field ({current})</SelectItem>}
                        {ghlFields.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </section>
          <ScoringCard points={s.points} setPoints={(p) => setS({ ...s, points: p })} refreshKey={syncCount} />
          <InsightsEditor />
          <TopicDescriptionsEditor />
        </div>

        <aside className="space-y-8">
          <section className="rounded-2xl bg-[#1F4266] p-6 text-white">
            <h2 className="text-xl font-bold">Setup guide</h2>
            <ol className="mt-4 space-y-4">
              {STEPS.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-white/85">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/15 font-mono text-xs">{i + 1}</span>
                  {t}
                </li>
              ))}
            </ol>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-3" data-testid="last-sync-panel">
            <h3 className="text-lg font-semibold text-slate-900">Last sync</h3>
            {!res ? <p className="text-sm text-slate-500">Not synced yet.</p> : (
              <div className="text-sm text-slate-600 space-y-1">
                <div>{fmtDate(s.last_sync_at)} · {new Date(s.last_sync_at).toLocaleTimeString()}</div>
                {res.contacts_scanned !== undefined && <div>{res.contacts_scanned} contacts scanned · {res.new_submissions} new · {res.report_links_sent} links sent</div>}
                {res.links_paused_in_preview && <div data-testid="links-paused-note" className="text-amber-700">Preview copy: report links are only written to GHL by the published app.</div>}
                {(res.errors || []).map((e, i) => <div key={i} className="text-red-600">{e}</div>)}
              </div>
            )}
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Sample data</h3>
            <p className="text-sm text-slate-500">{s.sample_count ? `${s.sample_count} sample entries (labelled “Sample”) are included so you can explore the reports.` : "Sample entries have been removed. Only real GHL results are shown."}</p>
            <Button data-testid="remove-samples-button" variant="outline" className="rounded-full text-red-600" onClick={removeSamples} disabled={!!busy || !s.sample_count}>
              <Trash2 className="mr-2 h-4 w-4" /> Remove sample entries
            </Button>
          </section>
        </aside>
      </div>
    </div>
  );
}
